/**
 * Runtime configuration: settings a deployed image reads at container start
 * from its environment (Infisical → pod env → /config.js), so changing one
 * is a pod restart rather than a rebuild.
 *
 * Only public, browser-visible settings belong here (the allowlist lives in
 * docker/runtime-config.sh). Build-time `import.meta.env` stays the fallback,
 * which is what local dev and tests use.
 */
export type RuntimeConfigKey =
  | 'VITE_CCN_ENABLED'
  | 'VITE_CCN_SIMULATOR_ENABLED'
  | 'VITE_CUBE_SERVICE_ID'
  | 'VITE_CUBE_CLIENT_ID'
  | 'VITE_CUBE_ENVIRONMENT'
  | 'VITE_CUBE_LOGIN_TYPE'
  | 'VITE_CUBE_REDIRECT_URL'
  | 'VITE_CUBE_AUTHORITY_URL'
  | 'VITE_CUBE_POLL_INTERVAL_MS'
  // CCN's JARVIS product page; unsubscribed CUBE users are sent there.
  | 'VITE_CUBE_PRODUCT_URL'
  // CMS feature flags (hooks/use-feature-flag): which environment's flags to
  // read ("prod" | "staging"), and the read-only consumer credential the
  // evaluation endpoint checks. The credential only reads flag values.
  | 'VITE_APP_ENV'
  | 'VITE_FEATURE_FLAG_KEY'
  // Comma list of CMS origins allowed to post a draft to /patch-log-preview.
  // Unset means both CMS hosts (features/patch-logs/preview-message).
  | 'VITE_PATCH_LOG_PREVIEW_ORIGINS';

type RuntimeConfig = Partial<Record<RuntimeConfigKey, string>>;

declare global {
  interface Window {
    __JARVIS_RUNTIME_CONFIG__?: RuntimeConfig;
  }
}

const fromWindow = (key: RuntimeConfigKey): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  const value = window.__JARVIS_RUNTIME_CONFIG__?.[key];
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
};

/** Runtime value first, build-time value second, undefined when neither is set. */
export const runtimeEnv = (key: RuntimeConfigKey): string | undefined => {
  const runtime = fromWindow(key);
  if (runtime !== undefined) return runtime;
  const built = (import.meta.env as Record<string, string | undefined>)[key];
  return typeof built === 'string' && built.trim() !== '' ? built.trim() : undefined;
};

/** Boolean flags are on only when the value is exactly "true". */
export const runtimeFlag = (key: RuntimeConfigKey): boolean => runtimeEnv(key) === 'true';
